/**
 * Lead detail screen (task 043-03) — route /leads/:id, reached by clicking a
 * row on the Leads screen. Everything held about one lead: received-at, IP,
 * landing page / CTO link provenance, traffic source + sub id, country, test
 * flag, Beehiiv status — and the shares table (which advertisers the lead was
 * shared with, and what they answered). The shares section always renders: no
 * attempts yet shows the empty-state line, never a hidden section.
 *
 * Task 057-02 adds the *Advertiser postbacks* section under Shares — every
 * inbound postback (epic 057) matched to this lead, newest first, with the
 * same always-render rule (none yet shows its own empty-state line).
 *
 * This file also hosts the leads feature's screen-local fetch layer. ADR-010
 * A2 (b): `lib/api.ts` is never edited; this task's fence grants exactly one
 * new screen file, so the endpoints — the search helpers the Leads screen
 * uses (email 043-03, IP 097-01) and this detail load — live here, restating
 * the same base-URL expression, `{ data }` envelope and `ApiError` as
 * `screens/distribution/api.ts`. Contracts come from `@leads-platform/shared`
 * — no local type mirror.
 *
 * Conventions (admin-requirements.md §1.4): MM-DD-YYYY dates, plain-language
 * labels, no visible DB ids except where ops quotes them (none here — the IP
 * is shown as stored, unmasked, per the task's out-of-scope list).
 */

import { useEffect, useState, type ComponentProps, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  type ApiResponse,
  type AttemptOutcome,
  type LeadDetailResponse,
} from '@leads-platform/shared';
import { apiFetch, ApiError, type LeadListWithLink } from '../lib/api';
import { PageHeader } from '../components/ui/page-header';
import { Card } from '../components/ui/card';
import { Tag } from '../components/ui/tag';
import { Button } from '../components/ui/button';
import { fmtDate } from '../lib/format';

/** The Tag tone union, derived from the component (not exported by tag.tsx). */
type TagTone = ComponentProps<typeof Tag>['tone'];

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

/**
 * One request over the admin api: unwrap `{ data }`, map every non-2xx to
 * `ApiError` (the same error type `lib/api.ts` throws — the screens'
 * `.catch((err: ApiError) => …)` idiom keeps working unchanged). Mirrors
 * `screens/distribution/api.ts`, which copies `lib/api.ts`'s private
 * `request()` (not exported).
 */
async function request<T>(path: string): Promise<T> {
  let response: Response;
  try {
    response = await apiFetch(`${API_BASE_URL}/admin${path}`, {
      headers: { 'Content-Type': 'application/json' },
    });
  } catch {
    throw new ApiError(0, 'Network error');
  }

  if (!response.ok) {
    const errorData: unknown = await response.json().catch(() => ({}));
    const message =
      typeof errorData === 'object' &&
      errorData !== null &&
      'message' in errorData &&
      typeof errorData.message === 'string' &&
      errorData.message.length > 0
        ? errorData.message
        : response.statusText;
    throw new ApiError(response.status, message);
  }

  const envelope: ApiResponse<T> = JSON.parse(await response.text());
  return envelope.data;
}

/** GET /admin/leads/:email-filtered list — the Leads screen search box. */
export function searchLeadsByEmail(email: string): Promise<LeadListWithLink> {
  const params = new URLSearchParams({ email });
  return request<LeadListWithLink>(`/leads?${params.toString()}`);
}

/** GET /admin/leads?ip=… — the search box's IP route (task 097-01, epic 097
 * D1): an exact `inet` match against the indexed `ip_address`. The screen
 * only sends this for IP-shaped input (its `looksLikeIp` sniff); a malformed
 * address still 400s from the api's query schema. */
export function searchLeadsByIp(ip: string): Promise<LeadListWithLink> {
  const params = new URLSearchParams({ ip });
  return request<LeadListWithLink>(`/leads?${params.toString()}`);
}

/** GET /admin/leads/:id — one lead's full record plus its shares. */
export function getLeadDetail(id: string): Promise<LeadDetailResponse> {
  return request<LeadDetailResponse>(`/leads/${encodeURIComponent(id)}`);
}

/** Shares outcome → Tag tone (the attempts-tab mapping, task 005-08). */
const OUTCOME_TONES: Record<AttemptOutcome, TagTone> = {
  accepted: 'sky',
  duplicate: 'amber',
  rejected: 'rose',
  suppressed: 'neutral',
  error: 'rose',
  timeout: 'rose',
  skipped: 'neutral',
};

/** Plain-language outcome labels (§1.4). */
const OUTCOME_LABELS: Record<AttemptOutcome, string> = {
  accepted: 'Accepted',
  duplicate: 'Duplicate',
  rejected: 'Rejected',
  suppressed: 'Suppressed',
  error: 'Error',
  timeout: 'Timeout',
  skipped: 'Skipped',
};

/** Beehiiv lookup status badge — the leads list mapping (task 024-14). */
function LookupStatusBadge({ status }: { status?: LeadDetailResponse['beehiivLookupStatus'] }) {
  switch (status) {
    case 'success':
      return <Tag tone="sky">Success</Tag>;
    case 'duplicate':
      return <Tag tone="amber">Duplicate</Tag>;
    default:
      return <span className="text-faint">— (not checked)</span>;
  }
}

/** Journey status badge — the leads list mapping. */
function JourneyStatusBadge({ status }: { status?: string | null }) {
  if (!status) return <span className="text-faint">--</span>;
  const toneMap: Record<string, TagTone> = { running: 'sky', completed: 'amber', failed: 'rose' };
  return (
    <Tag tone={toneMap[status] ?? 'sky'}>{status.charAt(0).toUpperCase() + status.slice(1)}</Tag>
  );
}

/** One label/value row of the detail grid. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-faint">{label}</div>
      <div className="mt-0.5 text-[13px] text-ink">{children}</div>
    </div>
  );
}

export function LeadDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [lead, setLead] = useState<LeadDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    getLeadDetail(id)
      .then(setLead)
      .catch((err: ApiError) =>
        setError(err.status === 404 ? 'Lead not found.' : `Error loading lead: ${err.message}`)
      )
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div>
        <PageHeader kicker="Operations" title="Lead" desc="Loading..." />
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div>
        <PageHeader kicker="Operations" title="Lead" />
        <div className="bg-destructive/10 text-destructive p-4 rounded-md">{error}</div>
        <Button className="mt-4" variant="outline" onClick={() => navigate('/leads')}>
          Back to leads
        </Button>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        kicker="Operations"
        title="Lead"
        desc={lead.emailOriginal}
        actions={
          <Button variant="outline" onClick={() => navigate('/leads')}>
            Back to leads
          </Button>
        }
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-6">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted">Capture</h2>
          <div className="grid grid-cols-2 gap-x-6 gap-y-4">
            <Field label="Received">
              {fmtDate(lead.createdAt)}
              <span className="ml-2 font-mono text-xs text-faint">{lead.createdAt}</span>
            </Field>
            <Field label="Email">
              <span className="font-mono text-xs">{lead.emailOriginal}</span>
              {lead.isTest ? (
                <span className="ml-2 rounded-full bg-amber/10 px-2 py-0.5 text-xs font-medium text-amber">
                  Test
                </span>
              ) : null}
            </Field>
            <Field label="IP address">
              {lead.ipAddress ? (
                <span className="font-mono text-xs">{lead.ipAddress}</span>
              ) : (
                <span className="text-faint">--</span>
              )}
            </Field>
            <Field label="Country">{lead.country || <span className="text-faint">--</span>}</Field>
            <Field label="Campaign">
              {lead.campaignName || <span className="text-faint">--</span>}
            </Field>
            <Field label="Landing page">
              {lead.pageSlug ? (
                <span className="font-mono text-xs">{lead.pageSlug}</span>
              ) : (
                <span className="text-faint">--</span>
              )}
            </Field>
            <Field label="Traffic source">
              {lead.siteId || lead.subId ? (
                <span className="font-mono text-xs">
                  {lead.siteId ?? '--'}
                  {lead.subId ? ` · sub ${lead.subId}` : ''}
                </span>
              ) : (
                <span className="text-faint">--</span>
              )}
            </Field>
            <Field label="Status">
              <LookupStatusBadge status={lead.beehiivLookupStatus} />
            </Field>
            <Field label="Journey">
              <JourneyStatusBadge status={lead.journeyStatus} />
            </Field>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted">
            CTO link
          </h2>
          {lead.campaignLinkId ? (
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              <Field label="Link">
                <span className="font-mono text-xs">
                  {lead.linkSiteLabel} · {lead.linkPromoSlug}
                </span>
              </Field>
              <Field label="Site id in URL">
                <span className="font-mono text-xs">{lead.linkSiteId}</span>
              </Field>
              <Field label="Link id">
                <span className="font-mono text-xs">{lead.campaignLinkId}</span>
              </Field>
            </div>
          ) : (
            <p className="text-[13px] text-faint">
              This lead did not come through a CTO link (capture style without link provenance).
            </p>
          )}
        </Card>
      </div>

      <Card className="mt-6 overflow-hidden">
        <div className="border-b border-line px-4 py-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">
            Shared with advertisers
          </h2>
        </div>
        {lead.shares.length === 0 ? (
          <p className="px-4 py-8 text-center text-[13px] text-faint">
            Sharing starts with the distribution release.
          </p>
        ) : (
          <table className="w-full min-w-[720px] text-left text-xs">
            <thead>
              <tr className="border-b border-line text-xs uppercase tracking-wide text-faint">
                <th className="px-4 py-2.5 font-semibold">Advertiser</th>
                <th className="px-4 py-2.5 font-semibold">Campaign</th>
                <th className="px-4 py-2.5 font-semibold">Outcome</th>
                <th className="px-4 py-2.5 font-semibold">Reason</th>
                <th className="px-4 py-2.5 text-right font-semibold">Sent</th>
              </tr>
            </thead>
            <tbody>
              {lead.shares.map((share) => (
                <tr key={share.id} className="border-b border-line/70">
                  <td className="px-4 py-3 text-muted">{share.advertiserName}</td>
                  <td className="px-4 py-3 text-muted">{share.campaignName}</td>
                  <td className="px-4 py-3">
                    <Tag tone={OUTCOME_TONES[share.outcome]}>{OUTCOME_LABELS[share.outcome]}</Tag>
                  </td>
                  <td className="px-4 py-3 text-muted">
                    {share.reason ?? <span className="text-faint">--</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-muted">
                    {fmtDate(share.sentAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card className="mt-6 overflow-hidden">
        <div className="border-b border-line px-4 py-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">
            Advertiser postbacks
          </h2>
        </div>
        {lead.postbacks.length === 0 ? (
          <p className="px-4 py-8 text-center text-[13px] text-faint">
            No advertiser postback received
          </p>
        ) : (
          <table className="w-full min-w-[720px] text-left text-xs">
            <thead>
              <tr className="border-b border-line text-xs uppercase tracking-wide text-faint">
                <th className="px-4 py-2.5 font-semibold">Received</th>
                <th className="px-4 py-2.5 font-semibold">Status</th>
                <th className="px-4 py-2.5 font-semibold">Newsletter</th>
                <th className="px-4 py-2.5 text-right font-semibold">Matched</th>
              </tr>
            </thead>
            <tbody>
              {lead.postbacks.map((postback) => (
                <tr key={postback.id} className="border-b border-line/70">
                  <td className="px-4 py-3 text-muted">
                    {fmtDate(postback.receivedAt)}
                    <span className="ml-2 font-mono text-xs text-faint">{postback.receivedAt}</span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-muted">
                    {postback.status ?? <span className="text-faint">--</span>}
                  </td>
                  <td className="px-4 py-3 text-muted">
                    {postback.newsletter ?? <span className="text-faint">--</span>}
                  </td>
                  <td className="px-4 py-3 text-right text-muted">
                    {postback.matchedBy ? (
                      `by ${postback.matchedBy}`
                    ) : (
                      <span className="text-faint">--</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
